import {Injectable} from '@angular/core';
import {
    AndConstraint,
    BetweenConstraint,
    ComparisonConstraint,
    Constraint,
    DeltaConstraint,
    DeltaDateConstraint,
    DynamicOperand,
    FieldTableDto,
    OrConstraint,
    PropertyType,
    PropertyValue,
    UpperCase,
    Value,
} from '@tj/tj-dmp-lib-api';
import * as moment from 'moment';
import {Criterion} from './filters/date-filter/criterion.interface';
import {Operator} from './filters/date-filter/operator.interface';

// node_modules/sib-api-v3-typescript/api'
@Injectable({
    providedIn: 'root',
})
export class QueryFactoryService {
    and(contraints: Constraint[]): Constraint {
        const andConstraint: AndConstraint = {
            '@type': 'AndConstraint',
            constraints: contraints,
        };
        return andConstraint;
    }

    or(contraints: Constraint[]): Constraint {
        const orConstraint: OrConstraint = {
            '@type': 'OrConstraint',
            constraints: contraints,
        };
        return orConstraint;
    }

    comparisonDate(field: FieldTableDto, criteria: Criterion[]): Constraint[] {
        const constraints: Constraint[] = [];
        let format = 'YYYY-MM-DD';
        if (field.categoryField === 'TIME') {
            format = 'hh:mm:ss';
        } else if (field.categoryField === 'DATE_TIME') {
            format = 'YYYY-MM-DD hh:mm:ss';
        }
        criteria.forEach((criterion) => {
            if (criterion.operator.code === 'range') {
                const date1 = moment(criterion.value[0]);
                const date2 = moment(criterion.value[1]);
                if (field.categoryField === 'DATE_TIME') {
                    date2.set({hour: 23, minute: 59, second: 59, millisecond: 999});
                }
                constraints.push(
                    this.between(field, date1.format(format), date2.format(format))
                );
            } else if (
                this.getCodeOperatorDelta().includes(criterion.operator.code)
            ) {
                constraints.push(
                    this.delta(
                        field,
                        criterion.operator.code,
                        criterion.operatorMoment,
                        criterion.delta
                    )
                );
            } else {
                const date = moment(criterion.value[0]).format(format);
                constraints.push(this.comparison(field, criterion.operator.code, date));
            }
        });
        return constraints;
    }

    delta(
        field: FieldTableDto,
        deltaType: string,
        operator: string,
        delta: number
    ): Constraint {
        const constraint: DeltaDateConstraint = {
            '@type': 'DeltaDateConstraint',
            delta: delta ? delta : 0,
            type: deltaType.toUpperCase() as DeltaConstraint.TypeEnum,
            operand1: this.propertyValue(field),
        };
        return constraint;
    }

    between(field: FieldTableDto, value1: any, value2: any): Constraint {
        const constraint: BetweenConstraint = {
            '@type': 'BetweenConstraint',
            operand1: this.propertyValue(field),
            fromOperand: this.value(field, value1),
            toOperand: this.value(field, value2),
        };

        return constraint;
    }

    propertyValue(field: FieldTableDto) {
        //
        let code = field.code;
        if (field.table) {
            code = field.table + '.' + code;
        }
        //
        const propertyValue: PropertyValue = {
            options: undefined,
            '@type': 'PropertyValue',
            propertyName: code
        };
        return propertyValue;
    }

    value(field: FieldTableDto, value: any) {
        let typeValue = PropertyType.STRING;
        if (field.categoryField === 'NUMBER') {
            typeValue = PropertyType.LONG;
        }
        const operand: Value = {
            '@type': 'Value',
            typeValue,
            value,
        };
        return operand;
    }

    comparison(
        field: FieldTableDto,
        operator: string,
        value: any,
        caseSensitive = true,
        typeSearch = '='
    ) {
        const propertyValue: PropertyValue = this.propertyValue(field);

        let operand1: DynamicOperand = propertyValue;
        if (caseSensitive === false) {
            operand1 = ({
                '@type': 'UpperCase',
                operand: propertyValue,
            } as UpperCase);
            value = String(value).toUpperCase();
        }

        if (typeSearch.includes('%')) {
            value = String(value) + '%';
        }
        if (typeSearch === '%%') {
            value = '%' + String(value);
        }

        let typeValue = PropertyType.STRING;
        if (field.categoryField === 'NUMBER') {
            typeValue = PropertyType.LONG;
        }

        const operand2: Value = this.value(field, value);

        const comparison: ComparisonConstraint = {
            '@type': 'ComparisonConstraint',
            operand1,
            operand2,
            operator: operator as ComparisonConstraint.OperatorEnum,
        };

        return comparison;
    }

    getDateOperators(): Operator[] {
        const operators: Operator[] = [
            {code: 'EQUALS', name: '='},
            {code: 'NOT_EQUALS', name: '≠'},
            {code: 'LESS_THAN', name: '<'},
            {code: 'LESS_THAN_OR_EQUAL', name: '≤'},
            {code: 'GREATER_THAN', name: '>'},
            {code: 'GREATER_THAN_OR_EQUAL', name: '≥'},
            {code: 'range', name: 'Période'},
            {code: 'day', name: 'Jour'},
            {code: 'week', name: 'Semaine'},
            {code: 'month', name: 'Moi'},
            {code: 'year', name: 'Année'},
        ];
        return operators;
    }

    getCodeOperatorDelta(): string[] {
        const delta = ['day', 'week', 'month', 'year'];
        return delta;
    }

    getDateOperator(code: string): Operator {
        return this.getDateOperators().find((item) => item.code === code);
    }
}
